/**
 * TDLS Builder — Shopify Variant ID Map (Runtime Fetch)
 *
 * Variant IDs are fetched at page load from Shopify's public
 * /products/<handle>.js endpoints — no static IDs or API keys needed.
 * Products must be available on the Online Store sales channel.
 *
 * Usage in builder.js:
 *   await window.__BUILDER_VARIANTS.ready;      // wait for IDs to load
 *   const variantId = findTierVariant(8.47);     // → nearest tier ≤ $8.47
 */

window.__BUILDER_VARIANTS = (function () {

  // Product handles (must match what was imported via CSV)
  var HANDLE_MAIN = 'custom-decorated-apparel';
  var HANDLE_ADJ_CENTS = 'order-adjustment-cents';
  var HANDLE_ADJ_DOLLARS = 'order-adjustment-dollars';

  // ─── Variant ID maps — populated at runtime ────────────────────
  var PRICE_TIERS = {};   // "8.50" → variant ID
  var ADJ_CENTS = {};     // "0.07" → variant ID
  var ADJ_DOLLARS = {};   // "3.25" → variant ID
  var TIER_PRICES = [];   // sorted ascending price numbers

  // ─── Fetch helpers ─────────────────────────────────────────────

  function fetchProductJSON(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Product fetch failed: ' + handle + ' (' + r.status + ')');
        return r.json();
      });
  }

  function parsePrice(optionVal) {
    // "$8.50" → "8.50"  or  "8.50" → "8.50"
    return optionVal.replace(/^\$/, '').trim();
  }

  function loadVariants() {
    return Promise.all([
      fetchProductJSON(HANDLE_MAIN),
      fetchProductJSON(HANDLE_ADJ_CENTS),
      fetchProductJSON(HANDLE_ADJ_DOLLARS),
    ]).then(function (products) {
      // Main price-tier product
      products[0].variants.forEach(function (v) {
        var key = parsePrice(v.option1);
        PRICE_TIERS[key] = v.id;
      });

      // Build sorted tier array
      TIER_PRICES = Object.keys(PRICE_TIERS)
        .map(Number)
        .sort(function (a, b) { return a - b; });

      // Adjustment cents
      products[1].variants.forEach(function (v) {
        var key = parsePrice(v.option1);
        ADJ_CENTS[key] = v.id;
      });

      // Adjustment dollars
      products[2].variants.forEach(function (v) {
        var key = parsePrice(v.option1);
        ADJ_DOLLARS[key] = v.id;
      });

    });
  }

  // Kick off fetch immediately; expose promise so callers can await
  var _ready = loadVariants();

  // ═══════════════════════════════════════════════════════════════
  //  PUBLIC API
  // ═══════════════════════════════════════════════════════════════

  /**
   * Find the best price-tier variant for a calculated per-unit price.
   * Rounds DOWN to the nearest tier so we never overcharge.
   * @param {number} unitPrice — true calculated price per unit
   * @returns {{ variantId: number, tierPrice: number } | null}
   */
  function findTierVariant(unitPrice) {
    var best = null;
    for (var i = TIER_PRICES.length - 1; i >= 0; i--) {
      if (TIER_PRICES[i] <= unitPrice + 0.001) {
        best = TIER_PRICES[i];
        break;
      }
    }
    if (best === null) best = TIER_PRICES[0]; // floor to minimum
    var key = best.toFixed(2);
    return {
      variantId: PRICE_TIERS[key],
      tierPrice: best,
    };
  }

  /**
   * Find adjustment variant(s) to close the rounding gap.
   * Returns an array of { variantId, quantity, amount } items.
   * Typically 0–2 items (one dollar adj + one cent adj).
   *
   * @param {number} gap — positive dollar amount to cover
   * @returns {Array<{ variantId: number, quantity: number, amount: number }>}
   */
  function findAdjustmentVariants(gap) {
    if (gap < 0.005) return []; // no adjustment needed

    var items = [];
    var remaining = Math.round(gap * 100) / 100; // avoid float issues

    // Dollar adjustments (handles $1.00+ portion; may require multiple items)
    if (remaining >= 1.00) {
      var dollarKeys = Object.keys(ADJ_DOLLARS).map(Number).sort(function (a, b) { return b - a; });
      var safety = 0;
      while (remaining >= 1.00 && safety < 200) {
        var picked = null;
        for (var i = 0; i < dollarKeys.length; i++) {
          if (dollarKeys[i] <= remaining + 0.001) {
            picked = dollarKeys[i];
            break;
          }
        }
        if (picked === null) break;

        var dk = picked.toFixed(2);
        items.push({
          variantId: ADJ_DOLLARS[dk],
          quantity: 1,
          amount: picked,
        });
        remaining = Math.round((remaining - picked) * 100) / 100;
        safety += 1;
      }
    }

    // Cent adjustment (handles $0.01–$0.99 remainder)
    if (remaining >= 0.005) {
      var centKey = remaining.toFixed(2);
      if (ADJ_CENTS[centKey]) {
        items.push({
          variantId: ADJ_CENTS[centKey],
          quantity: 1,
          amount: remaining,
        });
      }
    }

    return items;
  }

  function createBuilderBundleId(orderData) {
    if (orderData.editKey) return 'builder-' + orderData.editKey;
    return 'builder-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /**
   * Build the full set of /cart/add.js payloads for a builder order.
   *
   * @param {object} orderData — from the builder state
   * @param {number} orderData.unitPrice   — true per-unit price
   * @param {number} orderData.totalQty    — total units across all sizes
   * @param {object} orderData.garment     — garment object
   * @param {string} orderData.colorName   — selected color name
   * @param {string} orderData.colorHex    — selected color hex
   * @param {object} orderData.sizes       — { S: 2, M: 5, ... }
   * @param {object} orderData.locations   — decoration locations data
   * @param {string} orderData.notes       — special instructions
   * @param {string} [orderData.logoUrl]   — uploaded logo permanent URL
   * @returns {Array<{ id: number, quantity: number, properties: object }>}
   */
  function buildCartItems(orderData) {
    var tier = findTierVariant(orderData.unitPrice);
    var trueTotalPrice = orderData.unitPrice * orderData.totalQty;
    var tierTotalPrice = tier.tierPrice * orderData.totalQty;
    var gap = Math.round((trueTotalPrice - tierTotalPrice) * 100) / 100;
    var bundleId = createBuilderBundleId(orderData);

    // Format size breakdown: "S×2, M×5, L×10"
    var sizeBreakdown = Object.keys(orderData.sizes)
      .filter(function (s) { return orderData.sizes[s] > 0; })
      .map(function (s) { return s + '×' + orderData.sizes[s]; })
      .join(', ');

    // Format decoration summary
    var locLabels = orderData.locationLabels || {};
    var decoSummary = [];
    Object.keys(orderData.locations).forEach(function (loc) {
      var data = orderData.locations[loc];
      if (data && data.logoFile) {
        var method = (data.decoration || orderData.decoration).toUpperCase().replace('-', ' ');
        var locName = locLabels[loc] || loc.replace('-', ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        decoSummary.push(method + ' – ' + locName);
      }
    });

    var items = [];

    // Main item
    items.push({
      id: tier.variantId,
      quantity: orderData.totalQty,
      properties: {
        'Garment':          orderData.garment.name,
        'Brand':            orderData.garment.brand,
        'Style #':          orderData.garment.styleName || orderData.garment.styleID,
        'Color':            orderData.colorName,
        'Decoration':       decoSummary.join('; ') || 'None',
        'Print Size':       orderData.printSizeTier === 'lg' ? 'Oversized (up to 16×16\")' : 'Standard (up to 12×12\")',
        'Size Breakdown':   sizeBreakdown,
        'Notes':            orderData.notes || '',
        // Hidden from customer (underscore prefix)
        '_garment_id':            orderData.garment.id,
        '_color_hex':             orderData.colorHex,
        '_true_unit_price':       orderData.unitPrice.toFixed(2),
        '_tier_price':            tier.tierPrice.toFixed(2),
        '_total_gap':             gap.toFixed(2),
        '_decoration_json':       JSON.stringify(
          Object.keys(orderData.locations).reduce(function (acc, loc) {
            var d = orderData.locations[loc];
            if (d && d.logoFile) {
              acc[loc] = { method: d.decoration || orderData.decoration, scale: d.scale, rotation: d.rotation, printSize: d.printSize || null };
            }
            return acc;
          }, {})
        ),
        '_print_size':            orderData.printSizeTier || 'sm',
        '_builder_bundle_id':     bundleId,
        '_source':                'builder-v1',
        '_waive_digitizing':      orderData.waiveTapeFee ? 'yes' : 'no',
      },
    });

    // Artwork Locations URL property (visible — links are clickable in Shopify admin)
    if (orderData.logoUrl) {
      items[0].properties['Artwork Locations'] = orderData.logoUrl;
    }

    // Per-location artwork URLs — each gets its own property with a bare URL as
    // the value, so Shopify admin auto-renders it as a clickable link that opens
    // the file directly. Value MUST be a pure URL (no prefix) or Shopify will
    // display it as plain text instead of a link.
    if (orderData.logoUrlMap) {
      Object.keys(orderData.logoUrlMap).forEach(function (propName) {
        var url = orderData.logoUrlMap[propName];
        if (url) items[0].properties[propName] = url;
      });
    }

    // Preview mockup image — composite grid for admin (single clickable link)
    if (orderData.previewUrl) {
      items[0].properties['_preview_image'] = orderData.previewUrl;
    }

    // Individual preview slides for cart drawer slider (hidden from admin via underscore)
    if (orderData.previewSlides) {
      items[0].properties['_preview_slides'] = orderData.previewSlides;
    }

    // Edit key — allows returning to builder with full state restore
    if (orderData.editKey) {
      items[0].properties['_edit_key'] = orderData.editKey;
      // Visible clickable link for Shopify admin order view
      items[0].properties['Edit Order'] = 'https://thedirectlasersource.com/pages/custom-order-builder?edit=' + orderData.editKey;
    }

    // Adjustment items
    var adjustments = findAdjustmentVariants(gap);
    adjustments.forEach(function (adj) {
      items.push({
        id: adj.variantId,
        quantity: adj.quantity,
        properties: {
          '_type':              'price-adjustment',
          '_builder_bundle_id': bundleId,
          '_for_garment':       orderData.garment.name,
          '_gap_amount':        adj.amount.toFixed(2),
        },
      });
    });

    return items;
  }

  /**
   * Submit items to Shopify cart via AJAX API.
   * Waits for variant IDs to be loaded before submitting.
   * @param {Array} cartItems — from buildCartItems()
   * @returns {Promise<boolean>} — true if all items added successfully
   */
  async function addToShopifyCart(cartItems) {
    await _ready; // ensure variant IDs are loaded
    for (var i = 0; i < cartItems.length; i++) {
      var resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartItems[i]),
      });
      if (!resp.ok) {
        console.error('[Builder] Cart add failed for item', i, await resp.text());
        return false;
      }
    }
    return true;
  }

  // ═══════════════════════════════════════════════════════════════
  //  Export
  // ═══════════════════════════════════════════════════════════════
  return {
    ready: _ready,
    PRICE_TIERS: PRICE_TIERS,
    ADJ_CENTS: ADJ_CENTS,
    ADJ_DOLLARS: ADJ_DOLLARS,
    findTierVariant: findTierVariant,
    findAdjustmentVariants: findAdjustmentVariants,
    buildCartItems: buildCartItems,
    addToShopifyCart: addToShopifyCart,
  };

})();
